import React from 'react';
import { useThemeColors } from '@brandclub/bc-widgets';

import type { VoiceTranscriptLine } from '../hooks/useVoiceSession';

/**
 * `VoiceTranscriptBubble` — single line of live voice transcript, styled
 * to mirror the text-chat `ChatMessage` bubble language (decisions/078).
 *
 * Lightweight by design: it reads `themeColors.textPrimary` directly via
 * `useThemeColors()` and does NOT depend on `MessageBubbleWidget`,
 * `useWidgetConfig`, follow-up chips, action buttons, or anything else the
 * full `ChatMessage` orchestrates. The live voice overlay intentionally shows
 * NO action buttons below transcripts; the full button row (Copy / thumbs /
 * share) appears only after the call ends, on the text-chat `ChatMessage`.
 */
export interface VoiceTranscriptBubbleProps {
  line: VoiceTranscriptLine;
}

const USER_BUBBLE_BG = '#f6f8fa';

export const VoiceTranscriptBubble: React.FC<VoiceTranscriptBubbleProps> = ({ line }) => {
  const { themeColors } = useThemeColors();
  const textColor = themeColors.textPrimary || '#111827';

  if (line.role === 'user') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <div
          style={{
            maxWidth: '90%',
            background: USER_BUBBLE_BG,
            color: textColor,
            padding: '10px 14px',
            borderRadius: 18,
            // Match the text-surface message bubble (MessageBubbleWidget
            // defaults: 16px / 1.5) so there's no size jump on handoff.
            fontSize: 16,
            lineHeight: 1.5,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            opacity: line.partial ? 0.85 : 1,
          }}
        >
          {line.text}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <div
        style={{
          maxWidth: '100%',
          color: textColor,
          fontSize: 16,
          lineHeight: 1.5,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          opacity: line.partial ? 0.85 : 1,
        }}
      >
        {line.text}
      </div>
    </div>
  );
};

export default VoiceTranscriptBubble;
